/** Inspect changed subtrees instead of rescanning the document on every mutation. */
export function observeMatchingElements(selector: string, enhance: (element: HTMLElement) => void): () => void {
  const pending = new Set<HTMLElement>();
  let frame: number | undefined;
  const collect = (element: Element) => {
    if (element instanceof HTMLElement && element.matches(selector)) pending.add(element);
    element.querySelectorAll<HTMLElement>(selector).forEach(match => pending.add(match));
  };
  const flush = () => {
    frame = undefined;
    const elements = [...pending];
    pending.clear();
    elements.forEach(element => { if (element.isConnected) enhance(element); });
  };
  const observer = new MutationObserver(records => {
    for (const record of records) {
      const target = record.target instanceof Element ? record.target : record.target.parentElement;
      const ancestor = target?.closest<HTMLElement>(selector);
      if (ancestor) pending.add(ancestor);
      record.addedNodes.forEach(node => { if (node instanceof Element) collect(node); });
    }
    if (pending.size && frame === undefined) frame = window.requestAnimationFrame(flush);
  });
  observer.observe(document.body, { childList: true, subtree: true });
  document.querySelectorAll<HTMLElement>(selector).forEach(enhance);
  return () => { observer.disconnect(); if (frame !== undefined) cancelAnimationFrame(frame); pending.clear(); };
}
